import Big from "big.js";
import classNames from "classnames";
import { Text } from "components/commons";
import styles from "./detailed-transaction-history-list.module.scss";
import { dateTimeFormat, formatAmount, formatVolume } from "utils";

import { prettifyProductType, prettifyKeyPaymentMethod } from "utils/pretty.utils";
import DetailedTransactionHistoryRefNumbers from "./detailed-transaction-history-ref-numbers";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sortByItemId = (list) => [...(list ?? [])].sort((a, b) => a?.itemId - b?.itemId);

const roundBy = (value, ratio, decimalPlaces = 2) =>
  Number(Big(toNumber(value)).times(ratio).round(decimalPlaces));

const grossAmountOf = (item, ratio = 1) =>
  Number(
    Big(toNumber(item?.quantity)).times(toNumber(item?.originalPricePerItem)).times(ratio).round(2)
  );

const discountAmountOf = (item, ratio = 1) => {
  if (item?.originalTotalAmount != null && item?.netTotalAmount != null) {
    return roundBy(
      Big(toNumber(item.originalTotalAmount)).minus(toNumber(item.netTotalAmount)),
      ratio
    );
  }
  return roundBy(Math.abs(toNumber(item?.totalDiscount)), ratio);
};

const premiumDiscountOf = (item, ratio) => Number(Big(0).minus(discountAmountOf(item, ratio)));

const amountDueOf = (item, ratio) =>
  Number(Big(grossAmountOf(item, ratio)).minus(discountAmountOf(item, ratio)).round(2));

const quantityOf = (item, ratio) =>
  ratio === 1 ? item?.quantity : roundBy(item?.quantity, ratio, item?.type === "fuel" ? 3 : 2);

const LINE_HEIGHT = 24;

const sumAmounts = (paymentMethods) =>
  paymentMethods.reduce((sum, paymentMethod) => sum.plus(toNumber(paymentMethod?.amount)), Big(0));

const groupPaymentMethods = (paymentMethods) => {
  const groups = [];
  paymentMethods.forEach((paymentMethod) => {
    const group = groups.find(({ method }) => method === paymentMethod?.method);
    if (group) {
      group.paymentMethods.push(paymentMethod);
    } else {
      groups.push({ method: paymentMethod?.method, paymentMethods: [paymentMethod] });
    }
  });

  const totalPaid = sumAmounts(paymentMethods);

  if (groups.length <= 1 || totalPaid.lte(0)) {
    return [{ method: groups[0]?.method, paymentMethods, ratio: 1 }];
  }

  return groups.map((group) => ({
    ...group,
    ratio: Number(sumAmounts(group.paymentMethods).div(totalPaid)),
  }));
};

export const mapDataToList = ({ transactions, station }) => {
  const { items, processedAt, refName, receiptNumber, paymentMethods, shift } = transactions;

  const stationCode = station?.stationCode;
  const sortedItems = sortByItemId(items);
  const sortedPaymentMethods = sortByItemId(paymentMethods);

  const paymentGroups = groupPaymentMethods(sortedPaymentMethods);

  const mapGroups = (render) =>
    paymentGroups.map((group, index) => (
      <div
        key={`payment-group-${index}`}
        style={{
          minHeight: Math.max(2, sortedItems.length) * LINE_HEIGHT,
        }}
      >
        {render(group)}
      </div>
    ));

  const mapGroupItems = (render) =>
    mapGroups(({ ratio }) =>
      sortedItems.map((item, index) => (
        <Text key={`item-${index}`} className={classNames(styles.td, styles.line)}>
          {render(item, ratio)}
        </Text>
      ))
    );

  const mapItems = (render) =>
    sortedItems.map((item, index) => (
      <Text key={`item-${index}`} className={classNames(styles.td, styles.line)}>
        {render(item)}
      </Text>
    ));

  return {
    stationCode: <Text className={styles.td}>{stationCode ?? ""}</Text>,
    shiftDateAndTime: (
      <Text className={styles.td}>
        {dateTimeFormat(shift?.startShift)} - {dateTimeFormat(shift?.closedAt)}
      </Text>
    ),
    salesInvoiceNumber: (
      <Text className={styles.td}>{receiptNumber?.replace(/^0+(?=\d)/, "") ?? ""}</Text>
    ),
    transactionDateAndTime: <Text className={styles.td}>{dateTimeFormat(processedAt) ?? ""}</Text>,
    paymentMethod: (
      <>
        {mapGroups(
          ({ method }) =>
            method && (
              <Text className={classNames(styles.td, styles.line)}>
                {prettifyKeyPaymentMethod(method)}
              </Text>
            )
        )}
      </>
    ),
    paymentReferenceNumberAndName: (
      <>
        {mapGroups((group) => (
          <DetailedTransactionHistoryRefNumbers
            paymentMethods={group.paymentMethods}
            refName={refName}
          />
        ))}
      </>
    ),
    productType: <>{mapItems((item) => prettifyProductType(item?.type))}</>,
    productName: <>{mapItems((item) => item?.name)}</>,
    productDetails: (
      <>
        {mapItems((item) =>
          /^\d+$/.test(item?.variant) ? `Pump ${item?.variant}` : item?.variant
        )}
      </>
    ),
    quantity: (
      <>
        {mapGroupItems((item, ratio) =>
          item?.type === "fuel" ? formatVolume(quantityOf(item, ratio)) : quantityOf(item, ratio)
        )}
      </>
    ),
    totalizerReading: (
      <>
        {mapItems((item) =>
          item?.type === "fuel" ? item?.lastTotalInventory ?? "--" : " "
        )}
      </>
    ),
    pricePerItem: <>{mapItems((item) => formatAmount(item?.originalPricePerItem))}</>,
    totalGrossAmount: (
      <>{mapGroupItems((item, ratio) => formatAmount(grossAmountOf(item, ratio)))}</>
    ),
    totalDiscount: (
      <>
        {mapGroupItems((item, ratio) =>
          formatAmount(premiumDiscountOf(item, ratio), "₱", 2, 2, true)
        )}
      </>
    ),
    totalAmountDue: <>{mapGroupItems((item, ratio) => formatAmount(amountDueOf(item, ratio)))}</>,
  };
};

export const mapFilterToRequest = (filterState) => {
  const { statuses, ...fs } = filterState;

  return {
    ...fs,
    statuses: statuses.length === 0 ? [] : statuses.join(","),
  };
};
